import { useEffect, useRef, useState, type FormEvent, type ReactElement } from "react";

import { api, ApiError } from "../../api/client";
import { IRB_STUDY_LABELS } from "../../api/labels";
import type { AdminConsentVersion, AdminSubstudy } from "../../api/types";

// Consent info-sheet versions: list, upload (inactive), activate — same
// publish-then-activate shape as the Protocols card. Scope is an IRB study XOR
// a substudy; the select encodes both as "irb:<irb_study>" / "substudy:<id>".
export function ConsentVersionsCard({ substudies }: { substudies: AdminSubstudy[] }): ReactElement {
  const [versions, setVersions] = useState<AdminConsentVersion[]>([]);
  const [scope, setScope] = useState("");
  const [version, setVersion] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load(): Promise<void> {
    try {
      setVersions(await api<AdminConsentVersion[]>("GET", "/admin/consent-versions"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load consent versions.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function scopeLabel(v: AdminConsentVersion): string {
    if (v.irb_study !== null) return IRB_STUDY_LABELS[v.irb_study];
    const substudy = substudies.find((t) => t.id === v.substudy_id);
    return substudy ? `${substudy.name} (${substudy.slug})` : `substudy #${v.substudy_id}`;
  }

  async function upload(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!scope || !file) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const form = new FormData();
      const [kind, value] = scope.split(":", 2);
      form.append(kind === "irb" ? "irb_study" : "substudy_id", value ?? "");
      form.append("version", version.trim());
      form.append("file", file);
      const created = await api<AdminConsentVersion>("POST", "/admin/consent-versions", { form });
      setVersion("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setNotice(
        `Version "${created.version}" uploaded (inactive). Activate it to serve it to new consents.`,
      );
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not upload the consent version.");
    } finally {
      setBusy(false);
    }
  }

  async function activate(versionId: number): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const row = await api<AdminConsentVersion>(
        "POST",
        `/admin/consent-versions/${versionId}/activate`,
      );
      setNotice(`Version "${row.version}" is now served to new consents for its scope.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not activate the version.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="app-card">
      <h2>Consent Versions</h2>
      <p className="muted">
        The consent info sheets participants sign. Uploads register as inactive; activating a
        version makes it the one served to <em>new</em> consents for its scope; existing
        participants keep the version they signed.
      </p>
      {versions.length > 0 ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>Scope</th>
              <th>Version</th>
              <th>Status</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id}>
                <td>{scopeLabel(v)}</td>
                <td>
                  <code>{v.version}</code>
                </td>
                <td>{v.active ? "active" : "inactive"}</td>
                <td>{v.created_at.slice(0, 10)}</td>
                <td>
                  {!v.active ? (
                    <button
                      className="btn-ghost btn-sm"
                      disabled={busy}
                      onClick={() => void activate(v.id)}
                    >
                      Activate
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">No published consent versions yet; the built-in sheets are served.</p>
      )}
      <h3>Upload a New Version</h3>
      <form className="q-form" onSubmit={upload}>
        <div className="field-grid">
          <label className="field">
            <span>Scope (IRB study or substudy)</span>
            <select required value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="">Select…</option>
              {Object.entries(IRB_STUDY_LABELS).map(([irbStudy, label]) => (
                <option key={irbStudy} value={`irb:${irbStudy}`}>
                  {label}
                </option>
              ))}
              {substudies.map((t) => (
                <option key={t.id} value={`substudy:${t.id}`}>
                  Substudy: {t.name} ({t.slug})
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Version (becomes the filename, e.g. mgb_information_sheet_v1.3)</span>
            <input
              required
              value={version}
              maxLength={128}
              pattern="[A-Za-z0-9][A-Za-z0-9._-]*"
              onChange={(e) => setVersion(e.target.value)}
              placeholder="2025P002466_information_sheet_v1.3"
            />
          </label>
          <label className="field">
            <span>PDF</span>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,.pdf"
              required
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
        </div>
        <p>
          <button className="btn-ghost btn-sm" type="submit" disabled={busy}>
            Upload version
          </button>
        </p>
      </form>
      {notice ? <p className="muted">{notice}</p> : null}
      {error ? <p className="form-error">{error}</p> : null}
    </section>
  );
}
